













































































































import { Component, Vue, Prop, Watch, Ref } from "vue-property-decorator";
import { StateChanger } from "vue-infinite-loading";

import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";

import {
  ContactPersonInfo,
  ProjectDetails,
} from "@/data/models/ProjectDetails";
import { 
  ProjectEventLog, 
  ProjectNoteLog,
  ProjectLogSortOrder,
  ProjectLogFilter,
  ProjectLogData,
  getProjectEventLogSortOrderName,
  getProjectEventLogsFilterName,
  doesProjectEventLogMatchFilter,
  createProjectStatusLogs,
  ProjectLogType,
  ProjectSyncedNotes
} from "@/data/models/ProjectLog";
import { ProjectStepsContainer } from "@/data/models/ProjectSteps";
import { DryingProjectOverview } from "@/data/models/DryingProjectOverview";

import { getActiveUser } from "@/data/repository/UserRepository";

import ProjectLogItemComponent from "./ProjectLogItem.vue";
import { firestore } from "firebase";
import { timestampToDate } from "@/utils/DateFormatter";
import { joinToString } from "@/utils/LocalisationHelper";
import {
  applyDetailsSeparator,
  DetailsSeparator,
  calculateBackgroundStyle,
  nullIfUndefined,
  StorageKeyLastVisitedItemLevel,
} from "@/utils/Utils";
import ProjectNoteDialog from "./ProjectNoteDialog.vue";
import { COLOR_HEADER_ICON, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";

@Component({
  components: {
    "project-log-item": ProjectLogItemComponent,
    "menu-icon": MenuIconWithSelectionListComponent,
    "project-note-dialog": ProjectNoteDialog,
  },
})
export default class ProjectLogBookComponent extends Vue {
  @Prop({ default: false })
  showCompactVersion!: boolean;

  @Prop()
  projectOverview!: DryingProjectOverview;

  @Prop()
  projectDetails!: ProjectDetails;

  @Prop()
  projectSteps!: ProjectStepsContainer;

  @Prop()
  contactPersons!: ContactPersonInfo[];

  @Prop({ default: null })
  projectLogs!: ProjectLogData | null;

  @Prop({ default: null })
  projectSyncedNotes!: ProjectSyncedNotes | null;

  @Prop({ default: false })
  isReadOnlyMode!: boolean;

  @Ref("projectNoteDialog")
  projectNoteDialog!: ProjectNoteDialog;

  onError(data: string) {
    this.showErrorSnackbar(data);
  }

  onSuccess(data: string) {
    this.showSuccessSnackbar(data);
  }

  showErrorSnackbar(message: string) {
    this.errorSnackbar = true;
    this.errorSnackbarText = message;
  }

  showSuccessSnackbar(message: string) {
    this.successSnackbar = true;
    this.successSnackbarText = message;
  }

  isLoading = true;
  infiniteId = new Date();
  pageSize = 15;
  sliceLength = this.pageSize;

  sortOrders = [
    ProjectLogSortOrder.OLDEST_FIRST,
    ProjectLogSortOrder.NEWEST_FIRST
  ];
  selectedSortOrder = ProjectLogSortOrder.OLDEST_FIRST;

  allFilters = [
    ProjectLogFilter.ALL_LOGS,
    ProjectLogFilter.NOTE_AND_STATUS,
    ProjectLogFilter.NOTES,
  ];
  selectedFilter = ProjectLogFilter.NOTE_AND_STATUS;

  errorSnackbar = false;
  errorSnackbarText = "";
  successSnackbar = false;
  successSnackbarText = "";

  @Watch("selectedFilter")
  onFilterChanged() {
    this.resetDisplayedLogs();
  }

  @Watch("selectedSortOrder")
  onSortOrderChanged() {
    localStorage.setItem("projectLogsSortOrder", this.selectedSortOrder);

    this.resetDisplayedLogs();
  }

  get activeUser() {
    return getActiveUser();
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get headerIconColor() {
    return COLOR_HEADER_ICON;
  }

  get projectStatusLogs() {
    if (!this.projectSteps) {
      return [];
    }

    return createProjectStatusLogs(
      this,
      this.projectSteps,
      this.projectOverview,
      this.projectDetails
    );
  }

  get filteredAndSortedProjectLogs() {
    const eventLogs = this.projectLogs?.eventLogs as Record<string, ProjectEventLog>;
    const noteLogs = this.projectLogs?.noteLogs as Record<string, ProjectNoteLog>;
    const syncedNotes = this.projectSyncedNotes?.notes as Record<string, ProjectNoteLog>;

    const eventLogsArray = eventLogs ? Object.values(eventLogs) : [];
    const statusLogsArray = this.projectStatusLogs;
    const noteLogsArray = noteLogs ? Object.values(noteLogs) : [];
    const syncedNotesArray = syncedNotes ? Object.values(syncedNotes) : [];

    const filteredAndSortedLogsArray = ([
      ...eventLogsArray.map(log => ({ ...log, logType: ProjectLogType.EVENT })),
      ...statusLogsArray.map(log => ({ ...log, logType: ProjectLogType.STATUS })),
      ...noteLogsArray.map(log => ({ ...log, logType: ProjectLogType.NOTE })),
      ...syncedNotesArray.map(log => ({ ...log, logType: ProjectLogType.NOTE }))
    ]);

    return filteredAndSortedLogsArray
      .filter(log => doesProjectEventLogMatchFilter(this.selectedFilter, log))
      .sort((a, b) => {
        switch (this.selectedSortOrder) {
          case ProjectLogSortOrder.OLDEST_FIRST: {
            return this.sortByTimestamp(nullIfUndefined(b.createdBy?.recordedAt), nullIfUndefined(a.createdBy?.recordedAt));
          }

          default: {
            return this.sortByTimestamp(nullIfUndefined(a.createdBy?.recordedAt), nullIfUndefined(b.createdBy?.recordedAt));
          }
        }
      });
  }

  get numberOfLogsDisplayed() {
    return this.filteredAndSortedProjectLogs.length;
  }

  get projectEventLogsFilterIconRes() {
    return "mdi-filter-variant";
  }

  get selectedFiltersDescription() {
    const numberOfLogsDisplayed = this.numberOfLogsDisplayed;
    const numberOfLogsDisplayedText =
      this.$tc("projectLogs.numberOfLogsDisplayed", numberOfLogsDisplayed).toString();

    return joinToString(
      [
        getProjectEventLogsFilterName(this, this.selectedFilter).toString(),
        applyDetailsSeparator(numberOfLogsDisplayedText, DetailsSeparator.COLON)
      ],
      ""
    );
  }

  get showInfiniteLoading() {
    return this.slicedProjectLogsToDisplay.length > 0;
  }

  get slicedProjectLogsToDisplay() {
    return this.sliceProjectLogsToDisplay(this.sliceLength);
  }

  get sortOrderHeaderIconRes() {
    return "mdi-sort-alphabetical-variant";
  }

  async infiniteHandler(data: StateChanger) {
    const nextSliceLength = this.sliceLength + this.pageSize;
    const nextSlice = this.sliceProjectLogsToDisplay(nextSliceLength);

    this.sliceLength = nextSliceLength;

    if (nextSliceLength > nextSlice.length) {
      data.complete();
    } else {
      data.loaded();
    }
  }

  async mounted() {
    const cachedSortOrder = localStorage.getItem("projectLogsSortOrder");

    if (cachedSortOrder && this.selectedSortOrder !== cachedSortOrder) {
      this.selectedSortOrder = cachedSortOrder as ProjectLogSortOrder;
    }
  }

  async onAddNotes() {
    this.projectNoteDialog.show({
      isNewNote: true,
    });
  }

  getAllFiltersName() {
    let filtersName = "";
    
    const selectedFilterName = this.getProjectLogFilterName(this.selectedFilter);
    if (selectedFilterName !== this.getProjectLogFilterName(ProjectLogFilter.ALL_LOGS)) {
      filtersName += selectedFilterName;
    }

    return filtersName === ""
      ? this.$t("common.none").toString()
      : filtersName;
  }

  getSortOrderName(
    projectEventLogSortOrder: ProjectLogSortOrder
  ) {
    return getProjectEventLogSortOrderName(this, projectEventLogSortOrder);
  }

  getProjectLogFilterName(
    projectLogFilter: ProjectLogFilter,
  ) {
    return getProjectEventLogsFilterName(this, projectLogFilter);
  }

  resetDisplayedLogs() {
    this.sliceLength = this.pageSize;
    this.infiniteId = new Date();
  }

  sliceProjectLogsToDisplay(length: number) {
    const slicedProjectEventLogs = this.filteredAndSortedProjectLogs.slice(0, length);

    return slicedProjectEventLogs;
  }

  sortByTimestamp(
    timestampA: firestore.Timestamp | null,
    timestampb: firestore.Timestamp | null
  ) {
    const dateA = timestampA ? timestampToDate(timestampA) : null;
    const dateB = timestampb ? timestampToDate(timestampb) : null;

    if (dateA === null && dateB === null) {
      return 0;
    } else if (dateA === null) {
      return -1;
    } else if (dateB === null) {
      return 1
    }

    return dateB.getTime() - dateA.getTime();
  }

  calculateBackgroundStyle(logId: string) {
    return calculateBackgroundStyle(logId, StorageKeyLastVisitedItemLevel.LEVEL_5);
  }
}
